import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Chip, Typography, IconButton, Tooltip, Button, Checkbox, Select, MenuItem, FormControl, useTheme, CircularProgress, keyframes, Stack, Pagination, PaginationItem } from "@mui/material";
import { Save as SaveIcon, Close as CloseIcon, Archive as ArchiveIcon, Unarchive as UnarchiveIcon, ArrowForwardIos as ArrowForwardIosIcon, ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import ReusableTable from "../../../../../components/ReusableTable/ReusableTable";
import { formatDate } from "../../../../../utils/dateUtils";
import { calculateNextReviewDueDate } from "../../shared/hooks/useNextReviewDate";
import { useQuery } from "@apollo/client";
import { GET_STAFF_DETAILS } from "../../../../../apis/organization-apis/people-apis/peopleApis";
import Role from "../../../../../utils/roles";

const pulseAnimation = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(59, 130, 246, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(59, 130, 246, 0);
  }
`;

const AccountCountChip = ({ count }) => {
  const [isHovered, setIsHovered] = useState(false);
  const accountText = count === 1 ? 'Account' : 'Accounts';
  
  return (
    <Box
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        display: 'inline-flex',
        maxWidth: isHovered ? '120px' : '40px',
        transition: 'max-width 0.2s ease-out',
        overflow: 'hidden',
      }}
    >
      <Chip 
        label={isHovered ? `${count} ${accountText}` : `+${count}`}
        size="small" 
        sx={{ 
          borderRadius: '6px',
          fontWeight: 600,
          fontSize: '0.80rem',
          height: 24,
          backgroundColor: isHovered ? '#BFDBFE' : '#DBEAFE', 
          color: '#1E40AF',
          cursor: 'pointer',
          transition: 'background-color 0.3s ease-in-out',
          whiteSpace: 'nowrap',
          '& .MuiChip-label': {
            transition: 'opacity 0.2s ease-in-out',
            px: 1,
          },
        }} 
      />
    </Box>
  );
};

const OwnerCountChip = ({ count }) => {
  return (
    <Chip 
      label={`+${count}`}
      size="small" 
      sx={{ 
        borderRadius: '6px',
        fontWeight: 600,
        fontSize: '0.80rem',
        height: 24,
        backgroundColor: '#E5F7FF', 
        color: '#0277BD',
        whiteSpace: 'nowrap',
        '& .MuiChip-label': {
          px: 1,
        },
      }} 
    />
  );
};

const SystemsTable = ({ systems, onBulkUpdate, isEditMode = false, onEditModeChange, onArchiveSystem, archivingSystemId = null }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [editedData, setEditedData] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(() => {
    try {
      const stored = localStorage.getItem('accessManagementExpandedGroups');
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  });
  const [highlightedRowId, setHighlightedRowId] = useState(null);
  const location = useLocation();
  const [page, setPage] = useState(() => {
    const saved = sessionStorage.getItem('accessMgmtPage');
    return saved ? Number(saved) : 1;
  });
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { data: staffData } = useQuery(GET_STAFF_DETAILS);
  const allEmployees = staffData?.getEmployees?.data || [];
  
  const admins = useMemo(() => {
    return allEmployees
      .filter(emp => {
        return emp.roles?.some(role => role?.role_description?.toLowerCase() === Role.ADMIN.toLowerCase());
      })
      .map(emp => {
        const primaryEmail = emp.emails?.find((em) => em.type === "work")?.email || emp.emails?.[0]?.email || "";
        return {
          email: primaryEmail,
          name: emp.name || `${emp.first_name || ''} ${emp.last_name || ''}`.trim(),
        };
      })
      .filter(admin => admin.email);
  }, [allEmployees]);

  useEffect(() => {
    if (!isEditMode) {
      setSelectedRows(new Set());
      setEditedData({});
    }
  }, [isEditMode]);

  const handleToggleEditMode = () => {
    if (isEditMode) {
      setSelectedRows(new Set());
      setEditedData({});
    }
    if (onEditModeChange) {
      onEditModeChange(!isEditMode);
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedRows(new Set(systems.map(s => s.id)));
    } else {
      setSelectedRows(new Set());
      setEditedData({});
    }
  };

  const handleSelectRow = (systemId) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(systemId)) {
      newSelected.delete(systemId);
      const newEdited = { ...editedData };
      delete newEdited[systemId];
      setEditedData(newEdited);
    } else {
      newSelected.add(systemId);
    }
    setSelectedRows(newSelected);
  };

  const handleFieldChange = (systemId, field, value) => {
    setEditedData(prev => ({
      ...prev,
      [systemId]: {
        ...prev[systemId],
        [field]: value,
      }
    }));
  };

  const handleSaveChanges = async () => {
    if (!onBulkUpdate) return;

    setIsSaving(true);
    
    try {
      const updates = Array.from(selectedRows)
        .map(systemId => {
          const changes = editedData[systemId];
          if (!changes || Object.keys(changes).length === 0) return null;
          
          const updateObj = { systemId: String(systemId) };
          if (changes.system_admin !== undefined) updateObj.system_admin = changes.system_admin;
          if (changes.is_critical !== undefined) updateObj.is_critical = changes.is_critical;
          if (changes.is_review_active !== undefined) updateObj.is_review_active = changes.is_review_active;
          
          return updateObj;
        })
        .filter(Boolean);

      if (updates.length > 0) {
        await onBulkUpdate(updates);
      }

      if (onEditModeChange) {
        onEditModeChange(false);
      }
      setSelectedRows(new Set());
      setEditedData({});
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleGroup = (groupKey) => {
    setExpandedGroups(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(groupKey)) {
        newExpanded.delete(groupKey);
      } else {
        newExpanded.add(groupKey);
      }

      try {
        localStorage.setItem(
          'accessManagementExpandedGroups',
          JSON.stringify(Array.from(newExpanded))
        );
      } catch (error) {
        console.error('Failed to save expanded groups:', error);
      }
      
      return newExpanded;
    });
  };

  const visibleSystems = useMemo(() => {
    return systems.filter(system => 
      !system.isGroupChild || expandedGroups.has(system.groupKey)
    );
  }, [systems, expandedGroups]);

  const groupedRows = useMemo(() => {
    const groups = [];
    const processedGroupKeys = new Set();
    
    visibleSystems.forEach((row) => {
      if (row.isGroupHeader) {
        groups.push({
          groupKey: row.groupKey,
          rows: [row],
          rowCount: 1,
        });
        processedGroupKeys.add(row.groupKey);
      } else if (row.isGroupChild && row.groupKey) {
        const group = groups.find(g => g.groupKey === row.groupKey);
        if (group) {
          group.rows.push(row);
          group.rowCount += 1;
        } else {  
          groups.push({
            groupKey: row.groupKey || `orphan-${row.id}`,
            rows: [row],
            rowCount: 1,
          });
        }
      } else {
        groups.push({
          groupKey: `single-${row.id}`,
          rows: [row],
          rowCount: 1,
        });
      }
    });
    
    return groups;
  }, [visibleSystems]);


  // We iterate through groups and assign them to pages without splitting
  const { paginatedGroups, totalPages } = useMemo(() => {
    if (groupedRows.length === 0) {
      return { paginatedGroups: [], totalPages: 1 };
    }
    
    const pages = [];
    let currentPage = [];
    let currentPageRowCount = 0;
    
    for (const group of groupedRows) {
      if (currentPageRowCount + group.rowCount > rowsPerPage && currentPage.length > 0) {
        pages.push(currentPage);
        currentPage = [group];
        currentPageRowCount = group.rowCount;
      } else {
        currentPage.push(group);
        currentPageRowCount += group.rowCount;
      }
    }
    
    if (currentPage.length > 0) {
      pages.push(currentPage);
    }
    
    const totalPages = Math.max(1, pages.length);
    
    const currentPageGroups = pages[page - 1] || [];
    
    const paginatedRows = currentPageGroups.flatMap(group => group.rows);
    
    return { paginatedGroups: paginatedRows, totalPages };
  }, [groupedRows, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    sessionStorage.setItem('accessMgmtPage', String(newPage));
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(event.target.value);
    setPage(1);
  };

  useEffect(() => {
    if (!visibleSystems || visibleSystems.length === 0) return;
    
    try {
      const lastAccessedId = localStorage.getItem('accessManagementLastAccessedAccount');
      
      if (lastAccessedId && visibleSystems.some(s => String(s.id) === lastAccessedId)) {
        const accountRow = visibleSystems.find(s => String(s.id) === lastAccessedId);
        
        if (accountRow?.groupKey && !expandedGroups.has(accountRow.groupKey)) {
          setExpandedGroups(prev => {
            const newExpanded = new Set(prev);
            newExpanded.add(accountRow.groupKey);
            try {
              localStorage.setItem('accessManagementExpandedGroups', JSON.stringify(Array.from(newExpanded)));
            } catch (e) {
              console.error('Failed to save expanded groups:', e);
            }
            return newExpanded;
          });
          setTimeout(() => scrollToAccount(lastAccessedId), 500);
        } else {
          scrollToAccount(lastAccessedId);
        }
        
        setHighlightedRowId(lastAccessedId);
        
        setTimeout(() => {
          setHighlightedRowId(null);
          localStorage.removeItem('accessManagementLastAccessedAccount');
        }, 1000);
      }
    } catch (error) {
      console.error('Failed to restore last accessed account:', error);
    }
    
    function scrollToAccount(accountId) {
      setTimeout(() => {
        const rowIndex = visibleSystems.findIndex(s => String(s.id) === accountId);
        if (rowIndex >= 0) {
          const tableContainer = document.querySelector('.MuiTableContainer-root');
          if (tableContainer) {
            const tableRows = tableContainer.querySelectorAll('tbody tr');
            if (tableRows[rowIndex]) {
              tableRows[rowIndex].scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }
        }
      }, 200);
    }
  }, [visibleSystems, location.pathname, expandedGroups]);

  const selectStyles = {
    bgcolor: 'white',
    height: '36px',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#9CA3AF' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3B82F6', borderWidth: '2px' },
    '& .MuiSelect-select': { py: 0.75, fontSize: '0.875rem', fontWeight: 500 },
    '&.Mui-disabled': {
      bgcolor: '#F9FAFB',
      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' },
    }
  };

  const chipBaseStyles = {
    borderRadius: '6px',
    fontWeight: 600,
    fontSize: '0.75rem',
    height: 24,
    border: 'none',
  };

  const notApplicableText = (
    <Typography variant="body2" sx={{ color: '#9ca3af', fontStyle: 'italic' }}>
      Not Applicable
    </Typography>
  );

    const columns = [
      ...(isEditMode ? [{ label: "", field: "checkbox", align: "center", width: 50 }] : []),
      { label: "Name", field: "name", align: "left", width: "20%" },
      { label: "Source", field: "integration_type", align: "center", width: "17%", align: "left" },
      { label: "Owner", field: "system_admin", align: "center", width: "15%", align: "left" },
      { label: "Critical", field: "is_critical", align: "center", width: "10%", align: "left" },
      { label: "Next Review Due Date", field: "next_review_due_date", align: "center", width: "17%", align: "left" },
      ...(!isEditMode ? [{ label: "Action", field: "actions", align: "center", width: "9%" }] : []),
    ];
  
  const getCriticalColor = (isCritical) => {
    if (isCritical === null || isCritical === undefined) {
      return { bg: 'transparent', color: '#6B7280', label: '-' };
    }
    if (isCritical) {
      return { bg: '#FEE2E2', color: '#DC2626', label: 'Yes' };
    }
    return { bg: '#D1FAE5', color: '#059669', label: 'No' };
    };
  
    const renderCell = (row, column) => {
    const isSelected = selectedRows.has(row.id);
    const rowEdits = editedData[row.id] || {};

    if (column.field === "checkbox") {
      return (
        <Checkbox
          checked={isSelected}
          onChange={() => handleSelectRow(row.id)}
          disabled={isSaving && isSelected}
          sx={{ color: "#D1D5DB", "&.Mui-checked": { color: "#3B82F6" }, "&.Mui-disabled": { color: "#E5E7EB" } }}
        />
      );
    }

    if (column.field === "integration_type") {
      if (row.isGroupHeader) {
        if (row.integration_type === 'MIXED') {
          return (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {row.autoCount > 0 && (
                <Chip 
                  label={`${row.autoCount} Automated`} 
                  size="small" 
                  sx={{ ...chipBaseStyles, backgroundColor: '#DBEAFE', color: '#1E40AF' }} 
                />
              )}
              {row.manualCount > 0 && (
                <Chip 
                  label={`${row.manualCount} Manual`} 
                  size="small" 
                  sx={{ ...chipBaseStyles, backgroundColor: '#E5F7FF', color: '#0277BD' }} 
                />
              )}
            </Box>
          );
        }
        const isManual = (row.integration_type || '').toUpperCase() === 'MANUAL';
        return (
          <Chip
            label={isManual ? 'Manual' : 'Automated'}
            size="small"
            sx={{ ...chipBaseStyles, backgroundColor: isManual ? '#E5F7FF' : '#DBEAFE', color: isManual ? '#0277BD' : '#1E40AF' }}
          />
        );
      }
      
      const isManual = (row.integration_type || '').toUpperCase() === 'MANUAL';
      return (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {isManual ? 'Manual' : 'Automated'}
        </Typography>
      );
    }

      if (column.field === "actions") {
      if (row.isGroupHeader) {
        const isExpanded = expandedGroups.has(row.groupKey);
        return (
          <Tooltip title="View accounts" arrow>
            <IconButton
              size="medium"
              onClick={(e) => {
                e.stopPropagation();
                handleToggleGroup(row.groupKey);
              }}
              sx={{ color: theme.customColors?.icons?.dark || "#1E40AF", width: 40, height: 40, "&:hover": { backgroundColor: theme.palette?._blue?.light || "rgba(59, 130, 246, 0.1)" } }}
            >
              <ExpandMoreIcon sx={{ fontSize: 20, transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease-in-out' }} />
            </IconButton>
          </Tooltip>
        );
      }
      
      const isArchived = row.is_review_active === false;
      const isArchiving = archivingSystemId === row.id;
      
      return (
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", justifyContent: "center" }}>
          <Tooltip title={isArchived ? "Unarchive account" : "Archive account"} arrow>
            <IconButton
              size="medium"
              onClick={() => onArchiveSystem && onArchiveSystem(row)}
              disabled={isArchiving}
              sx={{
                color: isArchived ? "#10B981" : "#DC2626",
                width: 40,
                height: 40,
                "&:disabled": { opacity: 0.6, cursor: "not-allowed" },
              }}
            >
              {isArchiving ? <CircularProgress size={16} sx={{ color: isArchived ? "#10B981" : "#DC2626" }} /> : (isArchived ? <UnarchiveIcon sx={{ fontSize: 20 }} /> : <ArchiveIcon sx={{ fontSize: 20 }} />)}
            </IconButton>
          </Tooltip>
          <Tooltip title="View Details" arrow>
            <IconButton
              size="medium"
              onClick={() => {
                try {
                  localStorage.setItem('accessManagementLastAccessedAccount', String(row.id));
                } catch (error) {
                  console.error('Failed to save last accessed account:', error);
                }
                navigate(`/admin/access/system/${row.id}`);
              }}
              sx={{ color: theme.customColors?.icons?.dark || "#1E40AF", width: 40, height: 40, "&:hover": { backgroundColor: theme.palette?._blue?.light || "rgba(59, 130, 246, 0.1)", transform: "translateX(2px)", transition: "all 0.2s ease" } }}
            >
              <ArrowForwardIosIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
        );
      }

    if (column.field === "system_admin") {
      if (isEditMode && isSelected) {
        const currentValue = rowEdits.system_admin !== undefined ? rowEdits.system_admin : row.system_admin || '';
        return (
          <FormControl size="small" fullWidth>
            <Select
              value={currentValue}
              onChange={(e) => handleFieldChange(row.id, 'system_admin', e.target.value)}
              disabled={isSaving}
              displayEmpty
              renderValue={(selected) => {
                if (!selected) {
                  return <Typography sx={{ color: '#9CA3AF', fontSize: '0.875rem' }}>Select admin...</Typography>;
                }
                const admin = admins.find(a => a.email === selected);
                return admin ? admin.name : selected;
              }}
              sx={selectStyles}
            >
              <MenuItem value="" sx={{ fontSize: '0.875rem', py: 1, color: '#9CA3AF', fontStyle: 'italic' }}>None (Unassigned)</MenuItem>
              {admins.map(admin => (
                <MenuItem key={admin.email} value={admin.email} sx={{ fontSize: '0.875rem', py: 1 }}>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>{admin.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{admin.email}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      }
      
      if (row.isGroupHeader && row.adminNames && row.adminNames.length > 0) {
        const namesToShow = row.adminNames.slice(0, 1);
        const remainingCount = row.adminNames.length - namesToShow.length;
        const allOwners = row.adminNames;
        
        return (
          <Tooltip
            title={
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                  All Owners ({allOwners.length}):
                </Typography>
                {allOwners.map((name, idx) => (
                  <Typography key={idx} variant="caption" sx={{ display: 'block' }}>
                    • {name}
                  </Typography>
                ))}
              </Box>
            }
            arrow
            placement="top"
          >
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {namesToShow.map((name, index) => (
                <Chip
                  key={index}
                  label={name}
                  size="small"
                  sx={{
                    ...chipBaseStyles,
                    backgroundColor: '#E5F7FF',
                    color: '#0277BD',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                />
              ))}
              {remainingCount > 0 && (
                <OwnerCountChip count={remainingCount} />
              )}
            </Box>
          </Tooltip>
        );
      }
      
      const admin = admins.find(a => a.email === row.system_admin);
      return <Typography variant="body2" sx={{ fontWeight: 500 }}>{admin ? admin.name : row.system_admin || 'Unassigned'}</Typography>;
    }

    if (column.field === "is_critical") {
      if (isEditMode && isSelected) {
        const currentValue = rowEdits.is_critical;
        return (
          <FormControl size="small" fullWidth>
            <Select value={currentValue ? 'critical' : 'not_critical'} onChange={(e) => handleFieldChange(row.id, 'is_critical', e.target.value === 'critical')} disabled={isSaving} sx={selectStyles}>
              <MenuItem value="not_critical" sx={{ fontSize: '0.875rem', py: 1 }}>Not Critical</MenuItem>
              <MenuItem value="critical" sx={{ fontSize: '0.875rem', py: 1 }}>Critical</MenuItem>
            </Select>
          </FormControl>
        );
      }
      
      if (row.isGroupHeader && row.criticalCount !== undefined) {
        return (
          <Box>
            <Chip label={`${row.criticalCount} Critical`} size="small" sx={{ ...chipBaseStyles, backgroundColor: '#FEE2E2', color: '#DC2626', mb: 0.5 }} />
            {row.standardCount > 0 && (
              <Typography variant="caption" sx={{ display: 'block', color: theme.customColors?.text?.secondary || '#6B7280' }}>
                {row.standardCount} Non Critical
              </Typography>
            )}
          </Box>
        );
      }
      
      const criticalInfo = getCriticalColor(row.is_critical);
      return <Chip label={criticalInfo.label} size="small" sx={{ ...chipBaseStyles, backgroundColor: criticalInfo.bg, color: criticalInfo.color }} />;
    }

    if (column.field === "next_review_due_date") {
      if (row.isGroupHeader && row.earliestDueDate) {
        const earliestDate = row.earliestDueDate instanceof Date ? row.earliestDueDate : new Date(row.earliestDueDate);
        const formattedDate = formatDate(earliestDate);
        if (!formattedDate) return notApplicableText;
        
        const isOverdue = earliestDate < new Date();
        return (
          <Box>
            <Typography variant="body2" sx={{ color: isOverdue ? '#DC2626' : 'inherit', fontWeight: isOverdue ? 600 : 500 }}>{formattedDate}</Typography>
            <Typography variant="caption" sx={{ color: theme.customColors?.text?.secondary || '#6B7280' }}>Earliest due</Typography>
          </Box>
        );
      }
      
      const nextReviewDate = calculateNextReviewDueDate(row.last_reviewed_at, row.review_cycle, row.created_at);
      const formattedDate = formatDate(nextReviewDate);
      if (!formattedDate) return notApplicableText;
      
      const isOverdue = nextReviewDate < new Date();
      return <Typography variant="body2" sx={{ color: isOverdue ? '#DC2626' : 'inherit', fontWeight: isOverdue ? 600 : 'normal' }}>{formattedDate}</Typography>;
    }

    if (column.field === "name") {
      if (row.isGroupHeader) {
        const allAccounts = row.groupAccounts || [];
        const accountLabels = allAccounts
          .map(acc => acc.account_label || `Account ${acc.id}`)
          .filter(Boolean);
        
        return (
          <Tooltip
            title={
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                  All Accounts ({accountLabels.length}):
                </Typography>
                {accountLabels.map((label, idx) => (
                  <Typography key={idx} variant="caption" sx={{ display: 'block' }}>
                    • {label}
                  </Typography>
                ))}
              </Box>
            }
            arrow
            placement="top"
          >
            <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '16px' }}>{row.name}</Typography>
              <AccountCountChip count={row.accountCount} />
            </Box>
          </Tooltip>
        );
      }
      
      if (row.isGroupChild) {
        const groupChildren = visibleSystems.filter(s => 
          s.isGroupChild && s.groupKey === row.groupKey
        );
        const childIndex = groupChildren.findIndex(c => c.id === row.id);
        const isFirstChild = childIndex === 0;
        const isLastChild = childIndex === groupChildren.length - 1;
        
        return (
          <Box sx={{ position: 'relative', pl: 6, display: 'flex', alignItems: 'center', minHeight: '48px' }}>
            <Box
              sx={{
                position: 'absolute',
                left: 16,
                top: isFirstChild ? '-16px' : '-16px',
                bottom: isLastChild ? '50%' : '-16px', // Extend to connect with next row
                width: '2px',
                bgcolor: '#D1D5DB',
                zIndex: 0,
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                left: 16,
                top: '50%',
                width: '10px',
                height: '2px',
                bgcolor: '#D1D5DB',
                transform: 'translateY(-50%)',
                zIndex: 1,
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                left: 48,
                top: '20%',
                bottom: '20%',
                width: '2.5px',
                bgcolor: '#3B82F6',
                borderRadius: '2px',
                zIndex: 1,
              }}
            />
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 'bold', 
                fontSize: '14px',
                pl: 1.5,
                position: 'relative',
                zIndex: 2,
              }}
            >
              {row.account_label || `Account ${row.id}`}
            </Typography>
          </Box>
        );
      }
      
      return <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.name}</Typography>;
    }

      return row[column.field];
    };
  
  const renderHeaderCell = (column) => {
    if (column.field === "checkbox") {
      const allSelected = systems.length > 0 && selectedRows.size === systems.length;
      const someSelected = selectedRows.size > 0 && selectedRows.size < systems.length;
    return (
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected}
          onChange={handleSelectAll}
          sx={{ color: "#D1D5DB", "&.Mui-checked": { color: "#3B82F6" }, "&.MuiCheckbox-indeterminate": { color: "#3B82F6" } }}
        />
      );
    }
    return column.label;
  };

  const getRowSx = (row, rowIndex) => {
    const isChild = row.isGroupChild && expandedGroups.has(row.groupKey);
    const isHighlighted = highlightedRowId && String(row.id) === highlightedRowId;
    
    if (row.isGroupHeader) {
      const isExpanded = expandedGroups.has(row.groupKey);
      return {
        borderBottom: '2px solid #E5E7EB',
        borderTop: '1px solid #e0e0e0',
        bgcolor: isExpanded ? 'rgba(59, 130, 246, 0.05)' : 'transparent',
        transition: 'all 0.3s ease-in-out',
        cursor: 'pointer',
        ...(isHighlighted && {
          bgcolor: '#DBEAFE',
          borderLeft: '4px solid #3B82F6',
          animation: `${pulseAnimation} 1s ease-in-out`,
          boxShadow: '0 0 0 4px rgba(59, 130, 246, 0.3)',
        }),
        '&:hover': {
          bgcolor: isExpanded ? 'rgba(59, 130, 246, 0.08)' : 'rgba(59, 130, 246, 0.03)',
          transform: 'translateY(-1px)',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
        },
      };
    }
    
    if (isChild) {
      return {
        bgcolor: isHighlighted ? '#DBEAFE' : 'rgba(59, 130, 246, 0.05)', 
        transition: 'all 0.3s ease-in-out',
        borderTop: 'none !important',
        ...(isHighlighted && {
          borderLeft: '4px solid #3B82F6',
          animation: `${pulseAnimation} 1s ease-in-out`,
          boxShadow: '0 0 0 4px rgba(59, 130, 246, 0.3)',
        }),
        '&:hover': {
          bgcolor: isHighlighted ? '#BFDBFE' : 'rgba(59, 130, 246, 0.08)',
        },
      };
    }
    
    return {
      transition: 'all 0.3s ease-in-out',
      cursor: 'pointer',
      ...(isHighlighted && {
        bgcolor: '#DBEAFE',
        borderLeft: '4px solid #3B82F6',
        animation: `${pulseAnimation} 1s ease-in-out`,
        boxShadow: '0 0 0 4px rgba(59, 130, 246, 0.3)',
      }),
      '&:hover': {
        bgcolor: isHighlighted ? '#BFDBFE' : 'rgba(59, 130, 246, 0.03)',
        transform: 'translateY(-1px)',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
      },
    };
  };

  const getCellSx = (row, column, rowIndex, colIndex) => {
    const isChild = row.isGroupChild && expandedGroups.has(row.groupKey);
    
    if (isChild) {
      return {
        borderTop: 'none',
        borderBottom: 'none',
        py: 1.25,
      };
    }
    
    return {};
  };

  const getRowProps = (row, rowIndex) => {
    if (row.isGroupHeader) {
      return {
        onClick: () => handleToggleGroup(row.groupKey),
      };
    }
    return {};
  };
  
  return (
    <Box>
      {isEditMode && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, py: 2, bgcolor: selectedRows.size > 0 ? '#EFF6FF' : '#F9FAFB', borderBottom: `1px solid ${selectedRows.size > 0 ? '#BFDBFE' : '#E5E7EB'}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {selectedRows.size > 0 ? (
              <>
                <Chip label={`${selectedRows.size} system${selectedRows.size !== 1 ? 's' : ''} selected`} sx={{ bgcolor: '#DBEAFE', color: '#1E40AF', fontWeight: 600, fontSize: '0.875rem', height: 28, border: '1px solid #BFDBFE' }} />
                <Typography variant="body2" sx={{ color: theme.customColors.text.secondary }}>Use dropdowns below to update selected systems</Typography>
              </>
            ) : (
              <Typography variant="body2" sx={{ color: theme.customColors.text.secondary, fontStyle: 'italic' }}>Select accounts to edit their account owner, critical status, or status</Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button variant="outlined" startIcon={<CloseIcon sx={{ fontSize: 18 }} />} onClick={handleToggleEditMode} disabled={isSaving} sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px", fontSize: "14px", px: 3, py: 1, borderColor: theme.customColors.border.main, color: theme.customColors.text.secondary, '&:hover': { borderColor: '#9CA3AF', bgcolor: theme.customColors.background.light } }}>
              Cancel
            </Button>
            <Button variant="contained" startIcon={isSaving ? <CircularProgress size={18} sx={{ color: 'white' }} /> : <SaveIcon sx={{ fontSize: 18 }} />} onClick={handleSaveChanges} disabled={selectedRows.size === 0 || Object.keys(editedData).length === 0 || isSaving} sx={{ bgcolor: "#10B981", color: "white", fontWeight: 600, borderRadius: "8px", textTransform: "none", fontSize: "14px", px: 3, py: 1, boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', "&:hover": { bgcolor: "#059669", boxShadow: "0 2px 4px 0 rgba(0, 0, 0, 0.1)" }, "&:disabled": { bgcolor: theme.customColors.background.light, color: theme.customColors.text.disabled } }}>
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </Box>
      )}
      <ReusableTable columns={columns} data={paginatedGroups} renderCell={renderCell} renderHeaderCell={renderHeaderCell} getRowSx={getRowSx} getCellSx={getCellSx} getRowProps={getRowProps} showPagination={false} height="600px" />
      
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="flex-end"
        sx={{ padding: 2 }}
      >
        <FormControl variant="outlined" size="small">
          <Select value={rowsPerPage} onChange={handleChangeRowsPerPage}>
            {[10, 20, 40, 80].map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Pagination
          count={totalPages}
          page={page}
          onChange={handleChangePage}
          variant="outlined"
          shape="rounded"
          renderItem={(item) => (
            <PaginationItem
              {...item}
              sx={{
                background: item.selected ? "#031B89 !important" : "",
                color: item.selected ? "#fff !important" : "",
              }}
            />
          )}
        />
      </Stack>
    </Box>
    );
  };

  export default SystemsTable;